import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import useTranslation from '~/app/hooks/useTranslation';
import Icon, { MaterialIcons } from '~/app/common/Icon';
import LinkWrapper from '~/app/common/LinkWrapper';
import { route } from '~/app/helpers/route';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { WORKORDER_ROUTES } from '~/workorder/Router';
import { WorkOrderDetail, WorkOrderStatuses, WorkOrderTask, WorkOrderTaskStatus } from '~/workorder/types';
import classes from './style.module.scss';
import { useStoreActions, useStoreState } from '~/store';

export type TasksComponent = React.FC<{ data: WorkOrderDetail }>;

const Tasks: TasksComponent = ({ data }) => {
    const { t } = useTranslation('workorder');
    const [expanded, setExpanded] = useState(false);
    const taskCount = useStoreState(state => state.workOrder.taskCount);
    const setData = useStoreActions(state => state.workOrder.setData);
    const { total, done } = taskCount;

    const handleTaskStatusChange = useCallback((task: WorkOrderTask) => {
        if (data.status === WorkOrderStatuses.DELAYED || task.type === IssueOrServiceType.SERVICE) {
            return;
        }
        if (task.status === WorkOrderTaskStatus.INCOMPLETE) {
            task.status = WorkOrderTaskStatus.COMPLETED;
        } else if (task.status === WorkOrderTaskStatus.COMPLETED) {
            task.status = WorkOrderTaskStatus.REMOVED;
        } else if (task.status === WorkOrderTaskStatus.REMOVED) {
            task.status = WorkOrderTaskStatus.INCOMPLETE;
        }
        const newTask = data.tasks.map(t => t.id === task.id ? task : t);
        setData({...data, tasks: newTask});
    }, [setData, data]);

    const getIconProperties = (task: WorkOrderTask) => {
        if (task.status === WorkOrderTaskStatus.INCOMPLETE) {
            return {
                name: 'radio_button_unchecked',
                className: classes['unchecked'],
            };
        } else if (task.status === WorkOrderTaskStatus.COMPLETED) {
            return {
                name: 'check_circle',
                className: classes['checked'],
            };
        } else if (task.status === WorkOrderTaskStatus.REMOVED) {
            return {
                name: 'remove_circle',
                className: classes['removed'],
            };
        }
    }

    return (
        <div className={classes['card']}>

            <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
                <h2 className={classes['header-title']}>{t('detail.tasks')}</h2>
                <span className={classes['progress']}>{t('detail.tasksProgress', { done, total })}</span>
                <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
            </div>

            {expanded && (
                (total > 0 ?
                    <div className={classes['list']}>
                        {data.tasks.map(task => {
                            const icon = getIconProperties(task);
                            const isIssue = task.type === IssueOrServiceType.ISSUE;
                            const to = route(WORKORDER_ROUTES.taskDetail, {
                                id: data.id,
                                taskId: task.id,
                                type: isIssue ? 'issue' : 'service' 
                            });

                            return (
                                <div key={task.id} className={classes['row']}>
                                    {false && isIssue && <Icon name={icon?.name as MaterialIcons} size={22} className={icon?.className} onClick={() => handleTaskStatusChange(task)} />}
                                    <div
                                        className={classNames(
                                            classes['type-icon-wrap'],
                                            isIssue ? classes.issue : classes.service,
                                        )}
                                    >
                                        <Icon name={isIssue ? 'warning' : 'build'} />
                                    </div>
                                    <div className={classes['content']}>
                                        <p className={classNames(classes['task-title'], task.status === WorkOrderTaskStatus.INCOMPLETE ? classes.incomplete : classes.completed)}>{task.title}</p>
                                        <p className={classes['task-type']}>
                                            {t(`detail.taskType.${task.type}`)}
                                        </p>
                                    </div>
                                    <LinkWrapper to={to}>
                                        <Icon name="keyboard_arrow_right" className={classes['chevron']} />
                                    </LinkWrapper>
                                </div>
                            );
                        })}
                    </div>
                    : <p className={classes.empty}>{t('detail.noTasks')}</p>)
            )}
        </div>
    );
};

export default Tasks;
